import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Table,
  SearchBar,
  Pagination,
  Button,
  ConfirmDialog,
  type TableColumn,
  type SortConfig,
} from '../../components/aip';
import {
  getMinutesList,
  deleteMinutes,
  deleteMinutesBatch,
  type MeetingMinutesListItem,
} from '../../services/manageNewApi';

/**
 * 議事録一覧ページ
 */
export const MinutesListPage: React.FC = () => {
  // データ状態
  const [minutes, setMinutes] = useState<MeetingMinutesListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 検索・フィルター状態
  const [searchText, setSearchText] = useState('');

  // ページネーション状態
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // ソート状態
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: 'minutes_generated_at',
    direction: 'desc',
  });

  // 選択状態
  const [selectedKeys, setSelectedKeys] = useState<(string | number)[]>([]);

  // 削除ダイアログ状態
  const [deleteTarget, setDeleteTarget] = useState<MeetingMinutesListItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  // 一括削除ダイアログ状態
  const [showBatchDeleteDialog, setShowBatchDeleteDialog] = useState(false);
  const [batchDeleting, setBatchDeleting] = useState(false);

  // データ取得
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getMinutesList();
        setMinutes(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'データの取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // 日付フォーマット関数
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // フィルター処理
  const filteredData = useMemo(() => {
    let result = [...minutes];

    // 検索フィルター
    if (searchText) {
      const lowerSearch = searchText.toLowerCase();
      result = result.filter(
        (m) =>
          m.minutes_meeting_name?.toLowerCase().includes(lowerSearch) ||
          m.minutes_meeting_goal?.toLowerCase().includes(lowerSearch)
      );
    }

    // ソート処理
    if (sortConfig.key && sortConfig.direction) {
      result.sort((a, b) => {
        const aValue = (a as any)[sortConfig.key];
        const bValue = (b as any)[sortConfig.key];

        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        let comparison = 0;
        if (typeof aValue === 'string') {
          comparison = aValue.localeCompare(bValue);
        } else {
          comparison = aValue - bValue;
        }

        return sortConfig.direction === 'asc' ? comparison : -comparison;
      });
    }

    return result;
  }, [minutes, searchText, sortConfig]);

  // ページネーション処理
  const totalPages = Math.ceil(filteredData.length / pageSize);
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage, pageSize]);

  // ページ変更時に先頭ページに戻す
  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, pageSize]);

  // 単体削除処理
  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      setDeleting(true);
      await deleteMinutes(deleteTarget.id);
      setMinutes((prev) => prev.filter((m) => m.id !== deleteTarget.id));
      setDeleteTarget(null);
      setSelectedKeys((prev) => prev.filter((k) => k !== deleteTarget.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : '削除に失敗しました');
    } finally {
      setDeleting(false);
    }
  };

  // 一括削除処理
  const handleBatchDelete = async () => {
    if (selectedKeys.length === 0) return;

    try {
      setBatchDeleting(true);
      const ids = selectedKeys.map((k) => Number(k));
      await deleteMinutesBatch(ids);
      setMinutes((prev) => prev.filter((m) => !selectedKeys.includes(m.id)));
      setSelectedKeys([]);
      setShowBatchDeleteDialog(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : '一括削除に失敗しました');
    } finally {
      setBatchDeleting(false);
    }
  };

  // テーブルカラム定義
  const columns: TableColumn<MeetingMinutesListItem>[] = [
    {
      key: 'minutes_meeting_name',
      label: '会議名',
      sortable: true,
      sortType: 'text',
      render: (value, row) => (
        <Link className="aip-link" to={`/meeting/manage-new/minutes/${row.id}`}>
          {value || '（名称未設定）'}
        </Link>
      ),
    },
    {
      key: 'minutes_meeting_goal',
      label: '目的・ゴール',
      sortable: true,
      sortType: 'text',
      render: (value) => (
        <span
          style={{
            display: 'block',
            maxWidth: '300px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={value || ''}
        >
          {value || '-'}
        </span>
      ),
    },
    {
      key: 'minutes_generated_at',
      label: '議事録生成日時',
      sortable: true,
      sortType: 'date',
      render: (value) => formatDate(value),
    },
  ];

  // ヘッダーコンテンツ
  const headerContent = (
    <div className="aip-table__header-area">
      <div className="aip-table__header-buttons">
        <SearchBar
          value={searchText}
          onChange={setSearchText}
          placeholder="テーブル内検索"
          icon="manage_search"
        />
      </div>
      <div className="aip-table__header-buttons">
        {/* 議事録は作成ボタン不要 */}
      </div>
    </div>
  );

  // 選択時アクション
  const selectionActions = (
    <Button
      buttonType="icon-only"
      size="sm"
      state="alert"
      onClick={() => setShowBatchDeleteDialog(true)}
      aria-label="選択項目を削除"
    >
      <span className="material-symbols-outlined">delete</span>
    </Button>
  );

  // フッターコンテンツ
  const footerContent = (
    <Pagination
      currentPage={currentPage}
      totalPages={totalPages || 1}
      pageSize={pageSize}
      onPageChange={setCurrentPage}
      onPageSizeChange={setPageSize}
    />
  );

  // 操作カラム
  const renderActions = (row: MeetingMinutesListItem) => (
    <>
      <Button
        as="link"
        to={`/meeting/manage-new/minutes/${row.id}`}
        buttonType="icon-only"
        size="sm"
        aria-label="詳細"
      >
        <span className="material-symbols-outlined">visibility</span>
      </Button>
      <Button
        buttonType="icon-only"
        size="sm"
        state="alert"
        onClick={() => setDeleteTarget(row)}
        aria-label="削除"
      >
        <span className="material-symbols-outlined">delete</span>
      </Button>
    </>
  );

  // ローディング表示
  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
        読み込み中...
      </div>
    );
  }

  // エラー表示
  if (error) {
    return (
      <div
        style={{
          padding: '1rem',
          backgroundColor: 'var(--color-red50)',
          color: 'var(--color-alert)',
          borderRadius: '0.5rem',
        }}
      >
        {error}
      </div>
    );
  }

  return (
    <>
      <Table
        data={paginatedData}
        columns={columns}
        getRowKey={(row) => row.id}
        size="dense"
        selectable
        selectedKeys={selectedKeys}
        onSelectionChange={setSelectedKeys}
        selectionActions={selectionActions}
        sortConfig={sortConfig}
        onSortChange={setSortConfig}
        renderActions={renderActions}
        emptyMessage="議事録がありません"
        emptyDescription="会議終了後に議事録を生成してください"
        emptyIcon="description"
        headerContent={headerContent}
        footerContent={footerContent}
      />

      {/* 単体削除確認ダイアログ */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="議事録の削除"
        message={
          <>
            <strong>{deleteTarget?.minutes_meeting_name || '（名称未設定）'}</strong> の議事録を削除しますか？
            <br />
            この操作は元に戻せません。
          </>
        }
        confirmLabel="削除"
        cancelLabel="キャンセル"
        confirmType="alert"
        loading={deleting}
      />

      {/* 一括削除確認ダイアログ */}
      <ConfirmDialog
        open={showBatchDeleteDialog}
        onClose={() => setShowBatchDeleteDialog(false)}
        onConfirm={handleBatchDelete}
        title="議事録の一括削除"
        message={
          <>
            選択した <strong>{selectedKeys.length}件</strong> の議事録を削除しますか？
            <br />
            この操作は元に戻せません。
          </>
        }
        confirmLabel="削除"
        cancelLabel="キャンセル"
        confirmType="alert"
        loading={batchDeleting}
      />
    </>
  );
};

export default MinutesListPage;



